/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { IconProps } from '@coursera/cds-core';
import { Typography2, breakpoints } from '@coursera/cds-core';

import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import ModuleLayout, { isHorizontalLayout } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import Image from 'bundles/cds-labs/components/Modules/components/Image';

type Icon = React.ReactElement<IconProps>;

export type ValuePropItem = {
  id: string;
  description?: string | null;
  headline?: string | null;
  icon?: ImageType | Icon | null;
};

type Props = {
  description?: string | null;
  headline?: string | null;
  valueProps?: ValuePropItem[];
  cardBackgroundColor?: string;
} & ModuleLayoutConfig;

const styles = {
  headline: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  headlineNoBottomMargin: css`
    margin-bottom: calc(-1 * var(--cds-spacing-200));
  `,

  cardDescription: css`
    margin-top: var(--cds-spacing-100);
  `,
  cards: css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      gap: var(--cds-spacing-100);
    }
  `,

  headingVerticalLayout: css`
    margin-bottom: var(--cds-spacing-200);
    text-align: center;

    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-100);
    }
  `,

  card: (cardBasis: string, cardBackgroundColor?: string) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: var(--cds-spacing-300);
    background-color: ${cardBackgroundColor ?? 'var(--cds-color-neutral-background-primary)'};
    color: ${cardBackgroundColor ? 'inherit' : 'var(--cds-color-neutral-primary)'};
    border-radius: var(--cds-border-radius-100);
    gap: var(--cds-spacing-150);
    flex-basis: calc(${cardBasis} - var(--cds-spacing-400));

    ${breakpoints.down('xs')} {
      flex-basis: 100%;
      flex-direction: row;
      align-items: flex-start;
      text-align: left;
    }
  `,

  cardHorizontalLayout: css`
    align-items: flex-start;
    text-align: left;
  `,

  cardTwoColumns: css`
    flex-basis: calc(50% - var(--cds-spacing-400));
  `,
};

export function ValuePropsModule(props: Props) {
  const isHorizontal = isHorizontalLayout(props);

  // dynamically decide the card width
  const numCards = props.valueProps?.length || 0;
  let cardBasis = '50%';
  if (!isHorizontal && numCards > 4) {
    cardBasis = '33.3%';
  } else if (!isHorizontal) {
    cardBasis = `${100 / numCards}%`;
  }

  const cardPanel = () => (
    <div css={styles.cards}>
      {props.valueProps?.map((card) => {
        if (!card) return null;

        let icon: React.ReactNode = null;
        if (card.icon && 'url' in card.icon) {
          // the icon is an image object
          icon = <Image {...card.icon} />;
        } else if (card.icon) {
          // the icon is a CDS icon
          icon = card.icon as Icon;
        }

        return (
          <div
            css={[styles.card(cardBasis, props.cardBackgroundColor), isHorizontal ? styles.cardHorizontalLayout : null]}
            key={card.id}
          >
            {icon}
            <div>
              <Typography2 variant="subtitleLarge" component="h3" color="inherit">
                {card?.headline}
              </Typography2>
              <Typography2 variant="bodyPrimary" component="div" color="inherit" css={styles.cardDescription}>
                {card?.description}
              </Typography2>
            </div>
          </div>
        );
      })}
    </div>
  );

  const contentPanel = () => {
    // if title and description are empty, don't render anything to prevent extra spacing
    if (!props.description && !props.headline) {
      return null;
    }
    return (
      <div css={[isHorizontal ? null : styles.headingVerticalLayout]}>
        {props.headline && (
          <Typography2
            variant="titleSmall"
            component="h2"
            color="inherit"
            css={props.description ? styles.headline : styles.headlineNoBottomMargin}
          >
            {props.headline}
          </Typography2>
        )}

        {props.description && (
          <Typography2 variant="bodyPrimary" component="div" color="inherit">
            {props.description}
          </Typography2>
        )}
      </div>
    );
  };

  return <ModuleLayout assembly={props} renderContent={contentPanel} renderImage={cardPanel} />;
}

export default ValuePropsModule;
